import { useLayoutEffect, useRef } from "react";
import type { MutableRefObject } from "react";
import { FormPersistenceService } from "@/services/formPersistenceService";
import {
  type CreatePageState,
  useCreatePageStore,
} from "@/stores/createPageStore";
import {
  clearQuoteResumeAfterAuth,
  QUOTE_RESUME_ID_PARAM,
} from "@/utils/quoteResumePersistence";
import {
  clearCreditPurchaseAfterAuth,
  CREDIT_RESUME_ID_PARAM,
} from "@/utils/creditAuthIntentPersistence";
import {
  CREATE_AUTH_HANDOFF_PARAM,
  hasCreateAuthHandoff,
} from "@/utils/createAuthHandoffPersistence";
import {
  cancelActiveCreateAuthHandoff,
  cancelTrackedCreateAuthHandoff,
} from "@/utils/createAuthHandoffLifecycle";
import SavePoint from "@/utils/savePoint";
import {
  clearAgentSessionReset,
  clearStoredAgentSessionId,
} from "../utils/agentSessionId";

const CREATE_STATE_OWNER_KEY = "create.state.owner";

function readCreateStateOwner(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(CREATE_STATE_OWNER_KEY);
  } catch {
    return null;
  }
}

function writeCreateStateOwner(userId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (userId) window.sessionStorage.setItem(CREATE_STATE_OWNER_KEY, userId);
    else window.sessionStorage.removeItem(CREATE_STATE_OWNER_KEY);
  } catch {
    // The in-memory boundary still protects transitions during this mount.
  }
}

function isScopedAccountHandoffReturn(sourceUserId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URL(window.location.href).searchParams;
    return (
      params.get("restore_form") === "true" &&
      hasCreateAuthHandoff(params.get(CREATE_AUTH_HANDOFF_PARAM), sourceUserId)
    );
  } catch {
    return false;
  }
}

function clearCurrentTabHandoffArtifacts(): void {
  if (typeof window === "undefined") return;

  const activeHandoffId = cancelActiveCreateAuthHandoff();
  try {
    const url = new URL(window.location.href);
    const handoffId = url.searchParams.get(CREATE_AUTH_HANDOFF_PARAM);
    const quoteIntentId = url.searchParams.get(QUOTE_RESUME_ID_PARAM);
    const creditIntentId = url.searchParams.get(CREDIT_RESUME_ID_PARAM);

    if (handoffId && handoffId !== activeHandoffId) {
      cancelTrackedCreateAuthHandoff(handoffId);
    }
    if (quoteIntentId) {
      clearQuoteResumeAfterAuth(quoteIntentId);
      SavePoint.clear(quoteIntentId);
    }
    if (creditIntentId) {
      clearCreditPurchaseAfterAuth(creditIntentId);
      SavePoint.clear(creditIntentId);
    }

    [
      CREATE_AUTH_HANDOFF_PARAM,
      QUOTE_RESUME_ID_PARAM,
      CREDIT_RESUME_ID_PARAM,
      "restore_form",
      "quote_intent",
      "credit_intent",
      "credit_pack",
      "credit_gate",
      "_anon_work_adopted",
    ].forEach(param => url.searchParams.delete(param));
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  } catch {
    // The in-memory ownership reset still protects the new identity.
  }
}

interface SettledIdentity {
  initialized: boolean;
  userId: string | null;
}

interface UseCreateIdentityBoundaryParams {
  userId: string | null;
  authLoading: boolean;
  stateRef: MutableRefObject<CreatePageState>;
  isGeneratingRef: MutableRefObject<boolean>;
  identityEpochRef: MutableRefObject<number>;
}

/**
 * A null identity can legitimately become an authenticated identity when a
 * first anonymous session is minted or a signed-out draft is adopted during
 * sign-in. Once a concrete user has owned this mount, however, any different
 * settled identity is a hard ownership boundary.
 */
export function shouldResetForCreateIdentityTransition(
  previousUserId: string | null,
  nextUserId: string | null
): boolean {
  return previousUserId !== null && previousUserId !== nextUserId;
}

/**
 * Clears account-owned /create state when a long-lived page observes a real
 * auth identity change (for example account A signing out and anonymous user B
 * being minted without the React tree unmounting).
 *
 * Same-user token refreshes and anonymous-account upgrades keep the same
 * Supabase user id, so their in-progress dress remains intact. Initial auth
 * hydration is only observed after it settles and establishes the baseline;
 * it is never mistaken for an identity change.
 */
export function useCreateIdentityBoundary({
  userId,
  authLoading,
  stateRef,
  isGeneratingRef,
  identityEpochRef,
}: UseCreateIdentityBoundaryParams): void {
  const settledIdentityRef = useRef<SettledIdentity>({
    initialized: false,
    userId: null,
  });

  useLayoutEffect(() => {
    if (authLoading) return;

    const previousIdentity = settledIdentityRef.current;
    if (!previousIdentity.initialized) {
      const storedOwner = readCreateStateOwner();
      settledIdentityRef.current = { initialized: true, userId };
      writeCreateStateOwner(userId);
      if (!storedOwner || storedOwner === userId) return;

      // Existing-account sign-in and OAuth replace the user id after the
      // server adopts (or explicitly fails to adopt) the anonymous work. Keep
      // the durable save point until useInitialization restores that handoff.
      if (isScopedAccountHandoffReturn(storedOwner)) return;

      // The global Zustand store can outlive the component tree. A remount
      // under a different account must perform the same reset as an in-place
      // identity transition.
      previousIdentity.initialized = true;
      previousIdentity.userId = storedOwner;
    }

    const previousUserId = previousIdentity.userId;
    settledIdentityRef.current = { initialized: true, userId };
    writeCreateStateOwner(userId);
    if (!shouldResetForCreateIdentityTransition(previousUserId, userId)) {
      return;
    }

    const store = useCreatePageStore.getState();
    // Invalidate every async action before clearing state. Requests whose
    // transport cannot be aborted compare this epoch after each await and
    // stand down instead of applying Account A's response to user B.
    identityEpochRef.current += 1;
    // Prevent an old chat response from repopulating the freshly-cleared
    // store. Image generation has no transport abort, but releasing its local
    // one-shot guard ensures the new identity is not left permanently blocked.
    store.abortActiveRequest();
    isGeneratingRef.current = false;

    // Clear browser restoration sources before resetAll. Otherwise the
    // mount-only persistence effects could immediately replay account A's
    // ticket, variations, or conversation id for account/anonymous user B.
    FormPersistenceService.clearCanonicalState();
    clearStoredAgentSessionId();
    clearAgentSessionReset();
    clearCurrentTabHandoffArtifacts();
    store.resetAll();

    // Action callbacks read this ref rather than the render snapshot. Update
    // it synchronously in the same layout phase as the ownership reset.
    stateRef.current = useCreatePageStore.getState();
  }, [authLoading, identityEpochRef, isGeneratingRef, stateRef, userId]);
}
